import { useEffect, useRef, useCallback, useMemo } from "react";
import { FileText } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@web/components/ui/card";
import { Spinner } from "@web/components/ui/spinner";
import { EmptyState } from "@web/components/ui/empty-state";
import { LogListItem } from "@web/features/log/components/log-list-item";
import { useLogStore } from "@web/stores";
import { formatDateForInput, formatDateShort } from "@repo/share-log-web-utils";
import type { LogWithRelations } from "@repo/share-log-interfaces";

interface LogListProps {
  workspaceId: number;
  currentUserId: number;
  onClick: (log: LogWithRelations) => void;
  onEdit: (log: LogWithRelations) => void;
  onDelete: (logId: number) => void;
}

export function LogList({
  workspaceId,
  currentUserId,
  onClick,
  onEdit,
  onDelete,
}: LogListProps) {
  const { logs, hasMore, status, loadMore } = useLogStore();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const groupedLogs = useMemo(() => {
    const groups: {
      dateKey: string;
      label: string;
      logs: LogWithRelations[];
    }[] = [];
    for (const log of logs) {
      const dateKey = formatDateForInput(log.date);
      const last = groups[groups.length - 1];
      if (last && last.dateKey === dateKey) {
        last.logs.push(log);
      } else {
        groups.push({ dateKey, label: formatDateShort(dateKey), logs: [log] });
      }
    }
    return groups;
  }, [logs]);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore && status !== "loading") {
        void loadMore(workspaceId);
      }
    },
    [hasMore, status, loadMore, workspaceId],
  );

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    });

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>기록 목록</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {logs.length === 0 && status !== "loading" ? (
          <EmptyState
            icon={FileText}
            title="등록된 기록이 없습니다"
            description="새로운 기록을 추가해보세요."
          />
        ) : (
          <div>
            {groupedLogs.map(({ dateKey, label, logs: groupLogs }) => (
              <div key={dateKey}>
                <div className="sticky top-0 z-10 bg-muted/80 backdrop-blur-sm px-4 py-2 text-sm font-medium text-muted-foreground border-b">
                  {label}
                </div>
                {groupLogs.map((log) => (
                  <LogListItem
                    key={log.id}
                    log={log}
                    currentUserId={currentUserId}
                    onClick={onClick}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ))}
              </div>
            ))}
            <div ref={loadMoreRef} className="h-4" />
            {status === "loading" && (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
